import type { LanguageMap } from "@/services/category.service";
import { localizedText } from "./localization";
import type { TreeLike } from "./tree-model";

export const SEARCH_LIMIT = 200;

export interface IndexedCategory {
  id: string;
  name: string;
  path: string[];
  lowerName: string;
  /** Ancestors only, separator-normalised, so "bedroom" can qualify a name. */
  lowerPath: string;
}

export interface SearchEntry {
  id: string;
  name: string;
  path: string[];
  /** [start, end) within `name`, or null when the hit was in the path. */
  nameMatch: [number, number] | null;
  /** [start, end) within `path.join(" › ")`, or null. */
  pathMatch: [number, number] | null;
}

const PATH_SEPARATOR = " › ";

/** Collapses the display separator to spaces so a typed phrase can match. */
const normalise = (value: string) =>
  value.replace(/\s*›\s*/g, " ").replace(/\s+/g, " ").trim();

export function buildSearchIndex<T extends TreeLike<T> & { name: LanguageMap }>(
  roots: T[],
): IndexedCategory[] {
  const out: IndexedCategory[] = [];
  const walk = (list: T[], path: string[]) => {
    for (const node of list) {
      const name = localizedText(node.name);
      out.push({
        id: node.id,
        name,
        path,
        lowerName: name.toLowerCase(),
        lowerPath: normalise(path.join(PATH_SEPARATOR)).toLowerCase(),
      });
      walk(node.children, [...path, name]);
    }
  };
  walk(roots, []);
  return out;
}

/**
 * Matches a category by name, optionally qualified by its ancestors.
 *
 * The trailing words of the query must match the category's own name; any
 * leading words must appear in its ancestor path. So "storage" finds every
 * Storage, "bedroom storage" finds only the one under Bedroom, and "bedroom"
 * finds Bedroom itself rather than everything inside it.
 */
export function searchCategories(
  index: IndexedCategory[],
  query: string,
  limit: number = SEARCH_LIMIT,
): SearchEntry[] {
  const q = normalise(query.toLowerCase());
  if (!q) return [];
  const tokens = q.split(" ");

  const out: SearchEntry[] = [];
  for (const entry of index) {
    if (out.length >= limit) break;

    // Whole query against the name — the common case.
    const nameAt = entry.lowerName.indexOf(q);
    if (nameAt >= 0) {
      out.push({
        id: entry.id,
        name: entry.name,
        path: entry.path,
        nameMatch: [nameAt, nameAt + q.length],
        pathMatch: null,
      });
      continue;
    }

    // Otherwise split into an ancestor qualifier plus a name fragment, taking
    // the longest name fragment that still matches.
    let matched: SearchEntry | null = null;
    for (let k = 1; k < tokens.length && !matched; k++) {
      const qualifier = tokens.slice(0, k).join(" ");
      const fragment = tokens.slice(k).join(" ");
      if (!entry.lowerName.includes(fragment)) continue;

      const at = entry.lowerPath.indexOf(qualifier);
      if (at < 0) continue;

      const pathText = entry.path.join(PATH_SEPARATOR);
      const displayAt = pathText.toLowerCase().indexOf(qualifier);
      matched = {
        id: entry.id,
        name: entry.name,
        path: entry.path,
        nameMatch: null,
        pathMatch:
          displayAt >= 0
            ? [displayAt, Math.min(displayAt + qualifier.length, pathText.length)]
            : null,
      };
    }
    if (matched) out.push(matched);
  }
  return out;
}
